import React, { useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import { useOrganizationStore } from '../../store/organization.store';

/**
 * Delivery Overrides Editor — toggles for `settings.bypassQcRequirement` and
 * `settings.bypassReviewRequirement` on the current org.
 *
 * Visibility is gated per-org via `canManageDeliveryOverrides`. An org is
 * granted access by a Master-Org admin / super-admin in the Delivery Overrides
 * admin grid (organizations/{orgId}.deliveryOverridesEnabled), surfaced here as
 * the `deliveryOverridesEnabled` flag from the org store. Change that one
 * function to adjust who can see this editor — no UI changes needed.
 *
 * Writes to /organizations/{orgId} on save. The local store is updated
 * immediately on success so JobDetail's gate logic reflects the change without
 * waiting for an org switch.
 */

export function canManageDeliveryOverrides(opts: { deliveryOverridesEnabled: boolean }): boolean {
  return opts.deliveryOverridesEnabled === true;
}

interface Props {
  firebase: any;
}

const DeliveryOverridesEditor: React.FC<Props> = ({ firebase }) => {
  const currentOrgId = useOrganizationStore((s) => s.currentOrgId);
  const bypassQc = useOrganizationStore((s) => s.currentOrgBypassQc);
  const bypassReview = useOrganizationStore((s) => s.currentOrgBypassReview);
  const bypassQcBeforeReview = useOrganizationStore((s) => s.currentOrgBypassQcBeforeReview);
  const setOverrides = useOrganizationStore((s) => s.setCurrentOrgDeliveryOverrides);

  // Local working copy so the save button has a meaningful "dirty" state.
  const [localBypassQc, setLocalBypassQc] = useState(bypassQc);
  const [localBypassReview, setLocalBypassReview] = useState(bypassReview);
  const [localBypassQcBeforeReview, setLocalBypassQcBeforeReview] = useState(bypassQcBeforeReview);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Keep local copy in sync when the store changes (org switch, etc.).
  useEffect(() => { setLocalBypassQc(bypassQc); }, [bypassQc]);
  useEffect(() => { setLocalBypassReview(bypassReview); }, [bypassReview]);
  useEffect(() => { setLocalBypassQcBeforeReview(bypassQcBeforeReview); }, [bypassQcBeforeReview]);

  const isDirty =
    localBypassQc !== bypassQc ||
    localBypassReview !== bypassReview ||
    localBypassQcBeforeReview !== bypassQcBeforeReview;

  const handleSave = async () => {
    if (!firebase || !currentOrgId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(firebase.db, 'organizations', currentOrgId), {
        'settings.bypassQcRequirement': localBypassQc,
        'settings.bypassReviewRequirement': localBypassReview,
        'settings.bypassQcBeforeReview': localBypassQcBeforeReview,
      });
      setOverrides({
        bypassQc: localBypassQc,
        bypassReview: localBypassReview,
        bypassQcBeforeReview: localBypassQcBeforeReview,
      });
      setSuccess('Delivery overrides saved.');
    } catch (err: any) {
      console.error('Failed to save delivery overrides:', err);
      setError(err?.message || 'Could not save delivery overrides.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
        Your organisation has been granted access to bypass the standard delivery gates. When a
        bypass is on, campaigns can be delivered even if the matching check hasn't passed. A banner
        on each campaign indicates which overrides are active.
      </p>

      <ToggleRow
        label="Bypass QC requirement"
        description="Allow delivery without all linked tracks having passed technical QC."
        checked={localBypassQc}
        onChange={(v) => setLocalBypassQc(v)}
      />
      <ToggleRow
        label="Bypass review requirement"
        description="Allow delivery without all linked tracks having client approval."
        checked={localBypassReview}
        onChange={(v) => setLocalBypassReview(v)}
      />
      <ToggleRow
        label="Bypass QC-before-Review order"
        description="Default off. When off, a spot must pass QC before it can be shared for review — avoids re-sharing if a reviewed spot later fails QC. Turn on to allow review and QC in any order."
        checked={localBypassQcBeforeReview}
        onChange={(v) => setLocalBypassQcBeforeReview(v)}
      />

      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-sm btn-primary-custom btn-round"
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          <span>
            {saving ? (
              <><Spinner size="sm" className="me-2" />Saving…</>
            ) : 'Save overrides'}
          </span>
        </button>
        {!isDirty && !saving && (
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>No changes</span>
        )}
      </div>

      {error && (
        <div style={{ color: 'var(--button-danger)', marginTop: '0.5rem', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}
      {success && (
        <div className="text-success" style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
          {success}
        </div>
      )}
    </div>
  );
};

const ToggleRow: React.FC<{
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, description, checked, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0.5rem 0' }}>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        minWidth: '36px',
        height: '20px',
        borderRadius: '10px',
        border: 'none',
        backgroundColor: checked ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.2)',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        position: 'relative',
        padding: 0,
      }}
    >
      <div
        style={{
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          backgroundColor: '#fff',
          position: 'absolute',
          left: checked ? '18px' : '2px',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }}
      />
    </button>
    <div>
      <span style={{ color: '#fff' }}>{label}</span>
      <small className="text-muted d-block mt-1">{description}</small>
    </div>
  </div>
);

export default DeliveryOverridesEditor;
